import React from 'react'
import { format, getDay } from 'date-fns'

import { WEEKDAYS_NAMES } from 'settings/dayPicker'
import { Reservation } from 'models/Reservation'
import { ModalAction, ModalNames, CombinedModalState } from '../hooks/useReservationListItem'

import { 
  CancelRecurrentReservationModal,
  CancelNonRecurrentReservationModal, 
  CancelReservationDayModal,
  CancelReservationQuestionModal,
  Under24HourWarningModal,
  RescheduleNonRecurrentReservationModal,
  RescheduleReservationQuestionModal,
  ReschedulePerDateRecurrentReservationModal,
  RescheduleTotalRecurrentReservationModal, 
} from 'containers/ModalManager/Modals'

interface ReservationModalsProps {
  reservation: Reservation
  onRefresh: () => void
  modalDispatch: React.Dispatch<ModalAction>
  modalState: CombinedModalState
}

export const ReservationModals: React.FC<ReservationModalsProps> = ({
  reservation,
  modalDispatch,
  modalState,
  onRefresh
}) => {

  const {
    uuid,
    patientFullName,
    patientUsername,
    professionalFullName,
    professionalUsername,
    startDate,
    recurrencyExceptions
  } = reservation

  const weekday = WEEKDAYS_NAMES[getDay(startDate)]
  const hour = format(startDate, 'HH\'h\'mm')

  const closeModal = (modalName: ModalNames) => modalDispatch({
    modalName,
    type: 'CLOSE_MODAL'
  })

  const openModal = (modalName: ModalNames) => modalDispatch({
    modalName,
    type: 'OPEN_MODAL'
  })

  return (
    <>
      {modalState.CANCEL_RESERVATION_QUESTION_MODAL && (
        <CancelReservationQuestionModal 
          onClose={() => closeModal('CANCEL_RESERVATION_QUESTION_MODAL')}
          onSelectSingleDay={() => openModal('CANCEL_RESERVATION_DAY_MODAL')}
          onSelectAllReservation={() => openModal('CANCEL_RECURRENT_RESERVATION_MODAL')}
          patientFullName={patientFullName} 
          reservationHour={hour}
          reservationWeekday={weekday}
          isProfessional
        />
      )}
    
      {modalState.CANCEL_RESERVATION_DAY_MODAL && (
        <CancelReservationDayModal 
          onClose={() => closeModal('CANCEL_RESERVATION_DAY_MODAL')}
          patientFullName={patientFullName}
          patientUsername={patientUsername}
          professionalFullName={professionalFullName}
          professionalUsername={professionalUsername}
          reservationStartdate={new Date(startDate)}
          reservationUUID={uuid}
          isProfessional
          cancelledDates={recurrencyExceptions}
          onRefresh={onRefresh}
        />
      )}

      <CancelRecurrentReservationModal
        open={modalState.CANCEL_RECURRENT_RESERVATION_MODAL}
        onSetOpen={() => closeModal('CANCEL_RECURRENT_RESERVATION_MODAL')}
        onRefresh={onRefresh}
        isProfessional={true}
        reservationUUID={uuid}
        reservationHour={hour}
        reservationWeekday={weekday}
        patientUsername={patientUsername}
        patientFullName={patientFullName}
        professionalUsername={professionalUsername}
        professionalFullName={professionalFullName}
      />

      <CancelNonRecurrentReservationModal
        open={modalState.CANCEL_NON_RECURRENT_RESERVATION_MODAL}
        setOpen={() => closeModal('CANCEL_NON_RECURRENT_RESERVATION_MODAL')}
        onRefresh={onRefresh}
        reservationUUID={uuid}
        reservationDate={startDate}
        patientUsername={patientUsername}
        patientFullName={patientFullName}
        professionalUsername={professionalUsername}
      />

      {modalState.RESCHEDULE_RESERVATION_QUESTION_MODAL && (
        <RescheduleReservationQuestionModal 
          onClose={() => closeModal('RESCHEDULE_RESERVATION_QUESTION_MODAL')}
          onSelectReschedulePerDateReservation={() => openModal('RESCHEDULE_PER_DATE_RESERVATION_MODAL')}
          onSelectRescheduleTotalReservation={() => openModal('RESCHEDULE_TOTAL_RESERVATION_MODAL')}
        />
      )}
        
      {modalState.RESCHEDULE_PER_DATE_RESERVATION_MODAL && (
        <ReschedulePerDateRecurrentReservationModal
          handleClose={() => closeModal('RESCHEDULE_PER_DATE_RESERVATION_MODAL')}
          reservation={reservation}
          onRefresh={onRefresh}
        />
      )}

      {modalState.RESCHEDULE_TOTAL_RESERVATION_MODAL && (
        <RescheduleTotalRecurrentReservationModal 
          handleClose={() => closeModal('RESCHEDULE_TOTAL_RESERVATION_MODAL')}
          reservation={reservation}
          onRefresh={onRefresh}
        />
      )}
      
      {modalState.RESCHEDULE_NON_RECURRENT_RESERVATION_MODAL && (
        <RescheduleNonRecurrentReservationModal
          handleClose={() => closeModal('RESCHEDULE_NON_RECURRENT_RESERVATION_MODAL')}
          reservation={reservation}
          onRefresh={onRefresh}
        />
      )}

      <Under24HourWarningModal
        open={modalState.UNDER_24_HOUR_WARNING_MODAL}
        handleClose={() => closeModal('UNDER_24_HOUR_WARNING_MODAL')}
        action={modalState.actionType}
      />
    </>
  )
}